// Curated agent display-title choices (design 2g, extended by W1-06).
// Server-side enum lives on Agent.display_title / Agent.display_titles
// (migrations 0113 + 0137). Cosmetic only — no role/permission meaning.
// Keep in sync with the backend choices (Agent.DISPLAY_TITLE_CHOICES).
//
// W1-06 (fix wave 1):
// - Licensed members ALWAYS show the fixed "Agent" title first (system-derived
//   from is_licensed, not stored) — see getDisplayTitleLabels/DisplayTitleEditor.
// - The dropdown is completed with the standard football-organisation roles
//   already used by the platform's Staff vocabulary (staffRoles.js /
//   Staff.ROLE_TYPE_CHOICES) plus Agent and Board Member.
// - Multiple titles per person (up to MAX_DISPLAY_TITLES) and free custom
//   titles are stored in agent.display_titles; custom text is shown verbatim
//   and never translated.
//
// Agency-titles pass (Marin 2026-08-06): "diese ggf. hinzufügen nicht
// doppelt ... und Sachen wie Sportdirektor rausnehmen weil es das ja in der
// Agentur nicht gibt einen Sport Direktor und solche wirklich nur
// Vereinssachen." Frontend-only — Marin corrected the first pass of this:
// "diese Titel sind praktisch nur 'freitexte' ... und sollten in keiner
// Verbindung mit irgendetwas stehen ausser ihre Aufgabe zu machen im UI
// Titel Detail zu geben." These display titles are cosmetic free text
// end-to-end (the backend already accepts and stores up to 60 chars of any
// text verbatim, see validate_display_titles) — no backend enum entry
// needed for a new curated option:
// - AGENCY_TITLE_LABELS values ARE their own label text (e.g. "Marketing"),
//   so the stored token is plain readable text, exactly like a custom
//   title — just curated + translatable via the static bundle instead of
//   freely typed. No slug, no backend change.
// - The pre-existing football-organisation slugs (scout, board_member, …)
//   are untouched and keep resolving through ALL_TITLE_LABELS.
// - Club-only roles (Sporting Director, Technical Director, Head of
//   Football, Head Coach, Assistant Coach, Goalkeeping Coach, Youth Coach,
//   Academy Director, Youth Coordinator, Team Manager) are removed from
//   SELECTABLE_TITLE_VALUES below — an agency has no such role — while
//   staying resolvable via ALL_TITLE_LABELS so an agent who already has one
//   stored keeps seeing readable text, not a raw token.
// - True custom free-text titles (typed by the user, not from either list)
//   remain a separate, deliberately unrestricted path (Marin 2026-08-06:
//   "custom müssen auch nicht übersetzt werden sollen freiheit haben") and
//   are untouched here.

export const MAX_DISPLAY_TITLES = 5;

// Full backend vocabulary (Agent.DISPLAY_TITLE_CHOICES slugs) → label.
// Source of truth for resolving ANY stored slug, including ones no longer
// offered in the picker below (club-only roles).
const ALL_TITLE_LABELS = {
  scout: "Scout",
  sporting_consultant: "Sporting Consultant",
  head_of_recruitment: "Head of Recruitment",
  analyst: "Analyst",
  intermediary: "Intermediary",
  // W1-06 extension (2026-07): dropdown completed with the standard
  // football-organisation vocabulary already used by Staff.ROLE_TYPE_CHOICES
  // (plus Agent / Board Member).
  agent: "Agent",
  board_member: "Board Member",
  head_of_football: "Head of Football",
  sporting_director: "Sporting Director",
  director_of_football: "Director of Football",
  technical_director: "Technical Director",
  head_of_scouting: "Head of Scouting",
  chief_scout: "Chief Scout",
  youth_scout: "Youth Scout",
  head_of_analysis: "Head of Analysis",
  performance_analyst: "Performance Analyst",
  video_analyst: "Video Analyst",
  data_analyst: "Data Analyst",
  head_coach: "Head Coach",
  assistant_coach: "Assistant Coach",
  goalkeeping_coach: "Goalkeeping Coach",
  youth_coach: "Youth Coach",
  academy_director: "Academy Director",
  youth_coordinator: "Youth Coordinator",
  ceo: "CEO",
  president: "President",
  managing_director: "Managing Director",
  general_manager: "General Manager",
  team_manager: "Team Manager",
};

// Ordered, curated picker list of PRE-EXISTING slugs (Marin 2026-08-06).
// Club-only roles are deliberately absent here — see ALL_TITLE_LABELS above
// for why they still resolve.
const SELECTABLE_TITLE_VALUES = [
  // Agency / representation
  "agent",
  "intermediary",
  "sporting_consultant",
  // Leadership / board
  "board_member",
  "ceo",
  "president",
  "managing_director",
  "general_manager",
  // Football direction (agency-facing only)
  "director_of_football",
  // Scouting & recruitment
  "head_of_scouting",
  "head_of_recruitment",
  "chief_scout",
  "scout",
  "youth_scout",
  // Analysis
  "head_of_analysis",
  "analyst",
  "performance_analyst",
  "video_analyst",
  "data_analyst",
];

// Agency-titles extension (Marin 2026-08-06): plain free-text labels, not
// backend enum slugs (see file header). The value IS the label.
const AGENCY_TITLE_LABELS = [
  "Owner",
  "Executive Director",
  "Legal Counsel",
  "Partner",
  "Senior Manager",
  "Manager",
  "Player Service",
  "Marketing",
  "Accounting",
  "Business Development",
  "Fitness Coach",
  "Mental Coach",
  "Sports Psychologist",
  "Assistant",
];

export const DISPLAY_TITLE_OPTIONS = [
  { value: "", label: "No title" },
  ...SELECTABLE_TITLE_VALUES.map((value) => ({
    value,
    label: ALL_TITLE_LABELS[value],
  })),
  ...AGENCY_TITLE_LABELS.map((label) => ({ value: label, label })),
];

// Resolves any stored slug (including ones removed from the picker above,
// e.g. a previously-saved 'sporting_director') to readable text. Tokens not
// in this map — including every AGENCY_TITLE_LABELS entry and true custom
// text — fall through to the `token` itself in resolveTitleLabel below,
// which is already the correct display text for those.
const LABELS = ALL_TITLE_LABELS;

// The fixed label licensed members always carry (W1-06 part 1).
export const LICENSED_FIXED_LABEL = "Agent";

// Resolve one stored token (enum slug, agency-title label, or custom free
// text) to its label. Only legacy slugs need a lookup; everything else
// already IS its own label and is returned verbatim.
export const resolveTitleLabel = (token) =>
  token ? LABELS[token] || token : null;

// Stored title tokens for an agent: the W1-06 list when present, else the
// legacy single enum wrapped in an array (older cached rows).
export const getAgentTitleValues = (agent) => {
  if (!agent) return [];
  if (Array.isArray(agent.display_titles) && agent.display_titles.length > 0) {
    return agent.display_titles;
  }
  return agent.display_title ? [agent.display_title] : [];
};

// All labels to display for an agent. When `licensed` is true the fixed
// "Agent" label is prepended (system-derived, not stored) and de-duplicated
// against a stored 'agent' entry.
export const getDisplayTitleLabels = (agent, { licensed = false } = {}) => {
  const labels = getAgentTitleValues(agent).map(resolveTitleLabel).filter(Boolean);
  if (!licensed) return labels;
  return [
    LICENSED_FIXED_LABEL,
    ...labels.filter((l) => l !== LICENSED_FIXED_LABEL),
  ];
};

// Legacy helper (single title). Accepts either the raw enum
// (agent.display_title) or a server-provided agent.display_title_label.
export const getDisplayTitleLabel = (agent) => {
  if (!agent) return null;
  if (agent.display_title_label) return agent.display_title_label;
  const raw = agent.display_title;
  return raw ? LABELS[raw] || null : null;
};

export default getDisplayTitleLabel;
